import { computed, inject, Injectable, signal } from "@angular/core";
import { environment } from "../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { rxResource } from '@angular/core/rxjs-interop'
import { catchError, interval, map, Observable, of, Subscription, switchMap } from "rxjs";
import { LoginResponse, MenuItem, RoleInfo, AsignacionDto } from "../../interfaces/login-response.interface";
import { Router } from "@angular/router";
import { IResponse } from "../../interfaces/response";

type AuthStatus = 'checking' | 'authenticated' | 'not-authenticated';
const baseUrl = environment.baseUrl;

@Injectable({providedIn: 'root'})
export class AuthService {
  private _authStatus = signal<AuthStatus>('checking');
  private _user = signal<string|null>(null);
  private _token = signal<string|null>(localStorage.getItem('token'));
  private _refreshToken = signal<string|null>(localStorage.getItem('refreshToken'));

  // Datos de usuario
  private _usuarioId = signal<string|null>(localStorage.getItem('idUsuario'));
  private _usuarioEmail = signal<string|null>(localStorage.getItem('perfil'));
  private _usuarioNombre = signal<string|null>(localStorage.getItem('nombre'));
  private _personaNombreCompleto = signal<string|null>(localStorage.getItem('personaNombreCompleto'));

  // Roles y Menús
  private _roles = signal<RoleInfo[]>([]);
  private _menus = signal<MenuItem[]>([]);

  // Asignaciones disponibles - restaurar desde localStorage
  private _asignaciones = signal<AsignacionDto[]>(this.restaurarAsignacionesDelLocalStorage());

  private refreshSub?: Subscription;
  private http = inject(HttpClient);
  router = inject(Router);

  checkStatusResource = rxResource({
    loader: () => this.checkStatus()
  });

  // Computed properties públicos
  authStatus = computed<AuthStatus>(() => {
    if (this._authStatus() == 'checking') return 'checking';
    if (this._user()) {
      return 'authenticated';
    }
    return 'not-authenticated';
  })

  user = computed(() => this._user());
  token = computed(() => this._token());
  roles = computed(() => this._roles());
  menus = computed(() => this._menus());
  asignaciones = computed(() => this._asignaciones());
  usuarioId = computed(() => this._usuarioId());
  usuarioEmail = computed(() => this._usuarioEmail());
  usuarioNombre = computed(() => this._usuarioNombre());
  personaNombreCompleto = computed(() => this._personaNombreCompleto());

  getAsignaciones() {
    return this._asignaciones();
  }

  private restaurarAsignacionesDelLocalStorage(): AsignacionDto[] {
    const asignacionesJson = localStorage.getItem('asignaciones');
    if (asignacionesJson) {
      try {
        return JSON.parse(asignacionesJson);
      } catch (e) {
        console.error('Error restaurando asignaciones:', e);
        return [];
      }
    }
    return [];
  }

  login(username: string, password: string): Observable<boolean> {
    return this.http.post<any>(`${baseUrl}/Accounts/login`, {
      username: username,
      password: password,
      fingerprint: this.generateFingerprint()
    }).pipe(
      map(resp => {
        if (resp.success && resp.data) {
          // La respuesta ya viene con la estructura correcta
          return this.handleAuthSuccessLogin(resp.data as LoginResponse);
        }
        throw new Error(resp.message || 'Login fallido');
      }),
      catchError((error: any) => this.handleAuthError(error))
    );
  }

  /**
   * Genera un fingerprint basado en el user agent del navegador
   * Sirve para identificar el dispositivo/navegador
   */
  private generateFingerprint(): string {
    const userAgent = navigator.userAgent;
    const language = navigator.language;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fingerprint = `${userAgent}|${language}|${timezone}`;

    // Simple hash del fingerprint
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  }

  seleccionarContexto(asignacionId: number): Observable<any> {
    return this.http.post<any>(`${baseUrl}/Accounts/seleccionar-contexto`, {
      asignacionId: asignacionId
    }).pipe(
      map(resp => {
        if (resp.success && resp.data) {
          // Guardar el token contextual
          this.saveTokens(resp.data.accessToken, resp.data.refreshToken);
          return true;
        }
        return false;
      }),
      catchError((error: any) => {
        console.error('Error al seleccionar contexto:', error);
        return of(false);
      })
    );
  }

  refreshToken() {
    const refreshToken = localStorage.getItem('refreshToken');
    const token = localStorage.getItem('token');
    if (!refreshToken) return null;

    return this.http.post<any>(`${baseUrl}/Accounts/refresh-token`, {
      token: token,
      refreshToken: refreshToken,
      fingerprint: this.generateFingerprint()
    });
  }

  saveTokens(token: string, refreshToken: string) {
    this._token.set(token);
    this._refreshToken.set(refreshToken);
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
  }

  checkStatus(): Observable<boolean> {
    const token = localStorage.getItem('token');
    if (!token) {
      return of(false);
    }

    return this.http.get<any>(`${baseUrl}/Accounts/check-status`).pipe(
      map(resp => {
        if (resp.success && resp.data) {
          this._authStatus.set('authenticated');
          this._token.set(resp.data.token);
          this._user.set(resp.data.userName || resp.data.usuario_email);
          return true;
        }
        this.handleAuthError(null);
        return false;
      }),
      catchError((error: any) => {
        console.error('check-status failed, clearing session:', error);
        this.handleAuthError(error);
        return of(false);
      })
    )
  }

  logout() {
    this._user.set(null);
    this._token.set(null);
    this._refreshToken.set(null);
    this._usuarioId.set(null);
    this._usuarioEmail.set(null);
    this._usuarioNombre.set(null);
    this._personaNombreCompleto.set(null);
    this._roles.set([]);
    this._menus.set([]);
    this._authStatus.set('not-authenticated')

    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('perfil');
    localStorage.removeItem('nombre');
    localStorage.removeItem('idUsuario');
    localStorage.removeItem('personaNombreCompleto');

    this.stopTokenRefreshScheduler();
  }

  /**
   * Obtiene menús organizados jerárquicamente para un rol específico
   */
  getMenusByRole(roleId: string): MenuItem[] {
    const role = this._roles().find(r => r.rol_id === roleId);
    return role ? this.buildMenuHierarchy(role.menus) : [];
  }

  /**
   * Obtiene todos los menús combinados (útil para mostrar todos los menús disponibles)
   */
  getAllMenus(): MenuItem[] {
    return this._menus();
  }

  /**
   * Verifica si el usuario tiene un permiso específico en un menú
   */
  hasMenuPermission(menuId: number, permission: 'consultar' | 'registrar' | 'modificar' | 'eliminar' | 'exportar'): boolean {
    const menu = this.findMenuById(this._menus(), menuId);
    if (!menu) return false;
    return menu.permisos[permission] ?? false;
  }

  // ======================== PRIVADOS ========================

  private handleAuthSuccessCheck(resp: LoginResponse) {
    this._authStatus.set('authenticated');
    this._token.set(resp.token.accessToken);
    this._user.set(resp.usuario.email);
    localStorage.setItem('token', resp.token.accessToken);
    return true;
  }

  private handleAuthSuccessLogin(resp: LoginResponse) {
    // Guardar datos de usuario
    this._user.set(resp.usuario.email)
    this._usuarioEmail.set(resp.usuario.email);
    this._usuarioNombre.set(resp.usuario.iniciales);
    this._personaNombreCompleto.set(resp.usuario.nombreCompleto);
    this._usuarioId.set(resp.usuario.usuarioId);

    this._authStatus.set('authenticated');
    this._token.set(resp.token.accessToken);
    this._refreshToken.set(resp.token.refreshToken);

    // Guardar en localStorage para recuperar después
    localStorage.setItem('nombre', resp.usuario.iniciales);
    localStorage.setItem('perfil', resp.usuario.email ?? '');
    localStorage.setItem('personaNombreCompleto', resp.usuario.nombreCompleto);
    localStorage.setItem('token', resp.token.accessToken);
    localStorage.setItem('idUsuario', resp.usuario.usuarioId);
    localStorage.setItem('refreshToken', resp.token.refreshToken);

    // Guardar asignaciones disponibles
    this._asignaciones.set(resp.asignaciones);
    localStorage.setItem('asignaciones', JSON.stringify(resp.asignaciones));

    // Guardar la primera asignación como contexto por defecto
    if (resp.asignaciones && resp.asignaciones.length > 0) {
      localStorage.setItem('contextoActivo', JSON.stringify(resp.asignaciones[0]));
    }

    this.startTokenRefreshScheduler();

    return true;
  }

  private handleAuthError(error: any) {
    this._user.set(null);
    this._token.set(null);
    this._refreshToken.set(null);
    this._usuarioId.set(null);
    this._usuarioEmail.set(null);
    this._usuarioNombre.set(null);
    this._personaNombreCompleto.set(null);
    this._roles.set([]);
    this._menus.set([]);
    this._authStatus.set('not-authenticated');

    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('perfil');
    localStorage.removeItem('nombre');
    localStorage.removeItem('idUsuario');
    localStorage.removeItem('personaNombreCompleto');

    return of(false);
  }

  /**
   * Combina menús de múltiples roles y construye estructura jerárquica
   */
  private flattenAndMergeMenus(roles: RoleInfo[]): MenuItem[] {
    const menuMap = new Map<number, MenuItem>();

    // Recorrer todos los roles y sus menús
    roles.forEach(role => {
      role.menus.forEach(menu => {
        if (!menuMap.has(menu.menu_id)) {
          menuMap.set(menu.menu_id, JSON.parse(JSON.stringify(menu)));
        } else {
          // Merge permisos si el menú ya existe (tomar OR de permisos)
          const existing = menuMap.get(menu.menu_id)!;
          existing.permisos = {
            consultar: existing.permisos.consultar || menu.permisos.consultar,
            registrar: existing.permisos.registrar || menu.permisos.registrar,
            modificar: existing.permisos.modificar || menu.permisos.modificar,
            eliminar: existing.permisos.eliminar || menu.permisos.eliminar,
            exportar: existing.permisos.exportar || menu.permisos.exportar,
          };
        }
      });
    });

    // Construir estructura jerárquica
    return this.buildMenuHierarchy(Array.from(menuMap.values()));
  }

  /**
   * Construye estructura jerárquica de menús
   */
  private buildMenuHierarchy(menus: MenuItem[]): MenuItem[] {
    const menuMap = new Map<number, MenuItem>();
    const rootMenus: MenuItem[] = [];

    // Crear map de menús con array vacío de submenús
    menus.forEach(menu => {
      if (!menuMap.has(menu.menu_id)) {
        menuMap.set(menu.menu_id, { ...menu, submenu: [] });
      }
    });

    // Filtrar solo menús raíz y construir estructura
    menus.forEach(menu => {
      if (!menu.submenu || menu.submenu.length === 0) {
        // Si no tiene submenu explícito, es posible que sea un menú padre
        const mappedMenu = menuMap.get(menu.menu_id);
        if (mappedMenu) {
          rootMenus.push(mappedMenu);
        }
      }
    });

    // Si tiene submenu en la respuesta, usarlo directamente
    menus.forEach(menu => {
      if (menu.submenu && menu.submenu.length > 0) {
        const parentMenu = menuMap.get(menu.menu_id);
        if (parentMenu) {
          parentMenu.submenu = menu.submenu;
        }
      }
    });

    return rootMenus.sort((a, b) => a.menu_orden - b.menu_orden);
  }

  /**
   * Busca un menú por ID en la estructura jerárquica
   */
  private findMenuById(menus: MenuItem[], menuId: number): MenuItem | null {
    for (const menu of menus) {
      if (menu.menu_id === menuId) {
        return menu;
      }
      if (menu.submenu && menu.submenu.length > 0) {
        const found = this.findMenuById(menu.submenu, menuId);
        if (found) return found;
      }
    }
    return null;
  }


  startTokenRefreshScheduler() {
    this.stopTokenRefreshScheduler();

    this.refreshSub = interval(20 * 60 * 1000).pipe(
      switchMap(() => this.refreshToken() ?? of(null)),
      catchError(() => {
        this.logout();
        return of(null);
      })
    ).subscribe();
  }

  stopTokenRefreshScheduler() {
    this.refreshSub?.unsubscribe();
  }
}
